/**
 * @author    Nikko Renolds
 * @package		Trap Tycoon
 * @link		  https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments2026.
 * @license		MIT
 * @description Frontend component providing a shoutbox for real-time player messages and game event feeds.
 */
import React, { useState, useEffect, useRef } from "react";
import {
  Zap,
  ShieldAlert,
  Target,
  Skull,
  Trash2,
  ShieldOff,
  Smile,
} from "lucide-react";
import { callBackend } from "../utils/api";
import { useNotify } from "./Notification";

export default function Shoutbox({ socket, stats, onInspect }) {
  const { addNotification } = useNotify();
  const [messages, setMessages] = useState([]);
  const [tacticalMessages, setTacticalMessages] = useState([]);
  const [showFeed, setShowFeed] = useState(false);
  const [isTacticalMode, setIsTacticalMode] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);

  // Alliance check: stats now contains the alliance ID from profile/me
  const allianceId = stats?.alliance_id || null;
  const isStaff = stats?.role === "mod" || stats?.role === "dev";

  const EMOJIS = [
    "💰",
    "🔫",
    "❄️",
    "🔥",
    "💉",
    "🚓",
    "💀",
    "💵",
    "🤝",
    "💯",
    "🎲",
    "🔌",
  ];

  useEffect(() => {
    if (!socket) return;

    // Join rooms (Only once)
    if (stats?.id) socket.emit("join_private", stats.id);
    if (allianceId) socket.emit("join_alliance", allianceId);

    // Setup Listeners (Only once each)
    socket.on("new_shout", (msg) => setMessages((prev) => [msg, ...prev]));

    socket.on("alliance_msg", (msg) => {
      setTacticalMessages((prev) => [msg, ...prev]);
      if (!isTacticalMode) {
        addNotification(`Tactical incoming: ${msg.username}`, "info");
      }
    });

    socket.on("shout_deleted", (id) => {
      setMessages((prev) => prev.filter((m) => m.id !== parseInt(id)));
    });

    socket.on("shout_mention", (data) => {
      addNotification(`@${data.from} mentioned you in the streets!`, "success");
    });

    socket.on("game_feed", (event) => {
      if (showFeed)
        setMessages((prev) => [{ ...event, isFeed: true }, ...prev]);
    });

    // Initial Data Fetch
    callBackend("/shoutbox/fetch").then(setMessages);
    if (allianceId) {
      callBackend(`/alliance/shouts/${allianceId}`).then(setTacticalMessages);
    }

    // Cleanup
    return () => {
      socket.off("new_shout");
      socket.off("alliance_msg");
      socket.off("game_feed");
      socket.off("shout_deleted");
      socket.off("shout_mention");
    };
  }, [
    socket,
    showFeed,
    allianceId,
    isTacticalMode,
    addNotification,
    stats?.id,
  ]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent accidental form submission
      handlePostShout();
    }
  };

  const handlePostShout = async () => {
    if (!inputValue.trim()) return;
    try {
      const endpoint = isTacticalMode ? "/alliance/shout" : "/shoutbox/post";
      const body = isTacticalMode
        ? { allianceId, message: inputValue }
        : { message: inputValue };

      await callBackend(endpoint, { method: "POST", body });
      setInputValue("");
      setShowEmojis(false);
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Scrub this transmission?")) return;
    try {
      await callBackend(`/admin/shout/${id}`, { method: "DELETE" });
      addNotification("Message deleted.", "success");
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleMute = async (targetId) => {
    if (!window.confirm("Revoke shoutbox privileges?")) return;
    try {
      await callBackend("/admin/shoutbox/ban", {
        method: "POST",
        body: { targetId, status: true },
      });
      addNotification("User muted.", "success");
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const renderMessageText = (text) => {
    const parts = text.split(/(@\w+)/g);
    return parts.map((part, i) => {
      if (part.startsWith("@")) {
        return (
          <span
            key={i}
            className="text-yellow-500 font-bold hover:underline cursor-pointer"
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const activeMessages = isTacticalMode ? tacticalMessages : messages;

  return (
    <div
      className={`border-2 p-6 shadow-2xl font-mono transition-all duration-300 group
        ${
          isTacticalMode
            ? "border-blue-900/40 hover:border-blue-600 bg-blue-950/20"
            : "border-green-900/40 hover:border-green-600 bg-green-950/20"
        }`}
    >
      <div className="flex justify-between items-center mb-4 border-b border-zinc-800 pb-2">
        <div className="flex gap-2">
          <button
            onClick={() => setIsTacticalMode(false)}
            className={`text-[10px] font-black px-2 py-1 uppercase italic border ${
              !isTacticalMode
                ? "bg-green-600 text-black border-green-400"
                : "text-zinc-600 border-zinc-800"
            }`}
          >
            Public
          </button>
          {allianceId && (
            <button
              onClick={() => setIsTacticalMode(true)}
              className={`text-[10px] font-black px-2 py-1 uppercase italic border ${
                isTacticalMode
                  ? "bg-blue-600 text-white border-blue-400"
                  : "text-zinc-600 border-zinc-800"
              }`}
            >
              Tactical
            </button>
          )}
        </div>
        <button
          onClick={() => setShowFeed(!showFeed)}
          className={`text-[9px] px-2 py-1 border ${
            showFeed
              ? "bg-red-600 text-white border-red-400"
              : "text-zinc-500 border-zinc-800"
          }`}
        >
          <Zap size={10} className="inline mr-1" />{" "}
          {showFeed ? "FEED ON" : "FEED OFF"}
        </button>
      </div>

      <div className="space-y-3 h-[406px] overflow-y-auto mb-4 flex flex-col-reverse scrollbar-hide">
        {activeMessages.map((msg, i) => (
          <div
            key={msg.id || i}
            className={`text-[10px] group ${
              msg.isFeed ? "italic text-blue-400" : ""
            }`}
          >
            {msg.isFeed ? (
              <p className="flex items-center gap-1">
                <Zap size={8} /> {msg.message}
              </p>
            ) : (
              <div className="flex items-center flex-wrap">
                {!!msg.is_most_wanted && (
                  <span className="inline-flex items-center group relative mr-1">
                    <Skull size={12} className="text-yellow-500" />
                    <div className="absolute left-0 bottom-full mb-1 hidden group-hover:block bg-yellow-600 text-black text-[8px] px-1 font-black uppercase z-50">
                      Most Wanted
                    </div>
                  </span>
                )}
                {!!msg.is_top_hunter && (
                  <span className="inline-flex items-center group relative mr-1">
                    <Target size={12} className="text-red-500 animate-pulse" />
                    <div className="absolute left-0 bottom-full mb-1 hidden group-hover:block bg-red-900 text-white text-[8px] px-1 font-black uppercase z-50">
                      Elite Hunter
                    </div>
                  </span>
                )}
                {msg.tag && (
                  <span className="text-blue-500 font-black italic mr-1">
                    [{msg.tag}]
                  </span>
                )}
                <span
                  onClick={() => msg.user_id && onInspect(msg.user_id)}
                  className={`font-bold uppercase cursor-pointer hover:underline ${
                    isTacticalMode ? "text-blue-400" : "text-red-600"
                  } mr-1`}
                >
                  {msg.username}:
                </span>
                <span className="text-zinc-300">
                  {renderMessageText(msg.message)}
                </span>

                {isStaff && !isTacticalMode && (
                  <div className="hidden group-hover:flex gap-2 bg-black/80 px-2 py-1 border border-zinc-800 ml-2">
                    <button
                      onClick={() => handleMute(msg.user_id)}
                      className="text-orange-500 hover:text-orange-300"
                    >
                      <ShieldOff size={10} />
                    </button>
                    <button
                      onClick={() => handleDelete(msg.id)}
                      className="text-red-600 hover:text-red-400"
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="relative">
        {showEmojis && (
          <div className="absolute bottom-full left-0 mb-2 p-2 bg-zinc-900 border border-zinc-700 grid grid-cols-6 gap-1 z-50">
            {EMOJIS.map((e, index) => (
              <button
                key={`${e}-${index}`}
                onClick={() => setInputValue((p) => p + e)}
                className="hover:bg-zinc-800 p-1 text-sm"
              >
                {e}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isTacticalMode ? "Transmit to crew..." : "Shout something..."
            }
            className={`w-full bg-black border p-2 text-[10px] text-white outline-none uppercase transition-colors ${
              isTacticalMode
                ? "border-blue-900 focus:border-blue-500"
                : "border-zinc-800 focus:border-red-600"
            }`}
          />
          <button
            onClick={() => setShowEmojis(!showEmojis)}
            className="border border-zinc-800 px-3 text-zinc-500 hover:text-white"
          >
            <Smile size={14} />
          </button>
        </div>
        {isTacticalMode && (
          <ShieldAlert
            size={12}
            className="absolute right-12 top-2.5 text-blue-900"
          />
        )}
      </div>
    </div>
  );
}
